/**
 * 회원 > 제재관리 (/adm/member/penalty)
 * ($j.Pages.Admin.Penalty / body data-page-name="Penalty")
 *
 * **탐지부터 집행까지 한 화면.** 2026-08 에 감시(구 `/adm/collusion`)를 여기로 합쳤다 —
 * 짜고치기를 판정하면 「수발주제한」이 되고 재발주를 판정하면 「제재 목록」에 들어가는데,
 * 그 결과를 보려면 화면을 옮겨야 했다.
 *
 *   현황 · 랭킹 · 제재 목록    원장(지금 상태) — 이 클래스가 맡는다
 *   수발주제한 · -6차 회수      판정의 결과 — 짜고치기→수발주제한, 재발주→-6차 회수
 *
 * 짜고치기·재발주 **판정 목록은 알림 모아보기**(`/adm/tasks?type=collusion`)에 있다. 판정할 일을
 * 한 곳에 모으려고 여기 있던 두 탭을 걷어냈다. 판정창은 같은 공용 모듈($j.F.Admin.Collusion.Case)이다.
 * 재발주 판정에서 보유분이 없으면 회수예약이 자동으로 걸린다(Msix::reserveRecall).
 * 현황의 감시 KPI 는 Penalty/Collusion.js, -6차 회수는 Msix/Recall.js 가 맡는다 —
 * 그 판은 -6차 관리(/adm/msix 「지급/회수」탭)와 **같은 모듈·같은 블레이드 조각**이다.
 * 지급·즉시회수·예약이 화면 셋으로 갈려 있어 CS 가 한 회원을 처리하려고 옮겨다녔다.
 *
 * 탭 전환은 data-tab 으로 찾으므로
 * 순서를 바꿔도 코드는 그대로다 (blade 의 탭·pane 순서만 함께 맞춘다).
 *
 * 기사관리 모달($j.F.Admin.Member.Penalty)이 "이 회원이 왜 막혔나" 라면 원장 탭들은
 * **"지금 누가 막혀 있나"** 다. 그래서 목록의 기본 필터가 집행중이고 회원 이름이 앞에 온다.
 *
 * 부과·해제는 여기서 하지 않는다 — 회원을 특정해야 하는 일이라 기사편집으로 보낸다.
 * (해제는 사유를 받아야 하고, 부과는 죄목·처벌을 골라야 한다)
 * 예외가 감시 케이스다. 벌의 모양이 패턴마다 정해져 있어 판정창(모아보기)에서 끝난다.
 *
 * 설계: .ai/domains/penalty.md
 */
import Collusion from './Penalty/Collusion';
import Recall from './Msix/Recall';
import Util from '../../Common/Util';

export default class Penalty {
    initialize() {
        this.tab    = 'stats';
        this.$table = null;
        this.$rank  = null;
        this.$ban   = null;

        // 감시 파트. 현황이 첫 탭이라 start() 안에서 바로 KPI 를 불러온다
        this.co = new Collusion(this);
        this.co.start();

        // -6차 회수 판 — -6차 관리(/adm/msix) 「회수」탭과 공유하는 모듈
        this.recall = new Recall();
        this.recall.start();

        this.initTabs();
        this.initMemberInput();
        this.initBanForm();

        this.bindChargeOpen();
        this.bindMemberFocus();

        $('#f-search').on('click', () => this.reload());
        $('#f-state, #f-charge, #f-punish').on('change', () => this.reload());
        $('#f-mem, #f-reason').on('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.reload(); } });
        // 검색칸을 직접 지웠을 때도 띠가 따라 사라져야 한다 (자동완성으로 고르면 reload 가 부른다)
        $('#f-mem').on('input', () => this.syncWho());

        this.openFromQuery();
    }

    destroy() {
        if (this.co) this.co.destroy();
        if (this.recall) { this.recall.destroy(); this.recall = null; }
        [this.$table, this.$rank, this.$ban].forEach(t => t && t.destroy());
        this.$table = this.$rank = this.$ban = null;
    }

    /**
     * 주소로 탭·회원을 지정해서 연다.
     *
     *   ?tab=pair|reorder · ?open=케이스번호  옛 주소 — 판정 목록이 알림 모아보기로 옮겨갔다. 그리로 넘긴다
     *   ?mem=이름#번호  기사편집 제재현황의 '전과 N건' — 그 회원의 제재 목록으로
     *   ?tab=msix&mem=이름#번호  짜고치기 판정창의 「회수예약 확인」 — 그 회원의 -6차 판으로
     *
     * `mem` 값은 검색칸 규약(`이름#번호`) 그대로다. **주소에 보이는 텍스트가 곧 검색기준**이라
     * 사람이 주소를 고쳐도 그대로 먹는다.
     */
    openFromQuery() {
        const q   = new URLSearchParams(location.search);
        const tab = q.get('tab');
        const mem = q.get('mem');

        // 짜고치기·재발주 판정 목록은 모아보기로 옮겼다 — 즐겨찾기·옛 알림 주소를 그리로 넘긴다
        if (tab === 'pair' || tab === 'reorder' || q.get('open')) {
            const go = new URLSearchParams({ type: 'collusion', cs_type: tab === 'reorder' ? 'reorder' : 'pair' });
            if (q.get('open')) go.set('open', q.get('open'));
            location.replace('/adm/tasks?' + go.toString());
            return;
        }

        // -6차 판은 자기 회원칸(#mrc-mem)을 갖는다 — 제재 목록 검색칸과 다른 칸이라 따로 받는다.
        // 값은 검색칸 규약 그대로 `이름#번호` 이고, 그것을 넣어주면 Recall 이 알아서 판을 그린다
        if (tab === 'msix' && mem) {
            this.openTab('msix');
            this.recall.el('mem').val(mem).trigger('change');
            return;
        }

        // mem 이 있으면 목록 탭이 정해진 것이라 tab 은 보지 않는다 (focusMember 가 연다)
        if (mem) {
            const m = String(mem).trim().match(/^(.*?)#(\d+)$/);
            if (!m) { $('#f-mem').val(mem); this.openTab('list'); return; }

            this.focusMember(Util.int(m[2]), m[1]);
            return;
        }

        if (tab && tab !== 'stats' && $(`#pen-tabs a[data-tab="${tab}"]`).length) this.openTab(tab);
    }

    /** 탭 — 각 표는 그 탭을 처음 열 때 만든다 (열지 않으면 집계·목록 쿼리를 돌리지 않는다) */
    initTabs() {
        $('#pen-tabs').on('click', 'a', (e) => {
            e.preventDefault();
            this.openTab($(e.currentTarget).data('tab'));
        });
    }

    /**
     * 탭 전환 — 클릭과 프로그램 호출이 같은 길을 탄다.
     *
     * 판정창에서 "이 무리의 쌍만 보기" 로 넘어오거나(감시), 회원 모아보기로 목록에 오거나
     * 리다이렉트가 `?tab=` 으로 지정하는 등 코드가 탭을 여는 자리가 여럿이다. 클릭 핸들러
     * 안에 로직을 두면 그때마다 `.trigger('click')` 이 되고, 그러면 "지금 어느 탭인가"를
     * DOM 에서 되읽어야 한다.
     */
    openTab(tab) {
        if (!tab) return;
        this.tab = tab;

        $('#pen-tabs a').removeClass('active').filter(`[data-tab="${tab}"]`).addClass('active');
        // 판정 모달에도 [data-pane] 이 있을 수 있어 페이지 안으로 좁힌다
        $('#penRoot > .tab-content > .pen-pane').prop('hidden', true).filter(`[data-pane="${tab}"]`).prop('hidden', false);

        if (tab === 'list' && !this.$table) this.load();
        if (tab === 'rank' && !this.$rank)   this.loadRanking();
        if (tab === 'ban'  && !this.$ban)    this.loadBan();
        if (tab === 'msix') this.recall.open();
    }

    /**
     * 회원 검색칸 — 자동완성.
     *
     * 고르면 `이름#번호` 가 들어가고 **그 텍스트가 곧 검색기준**이다. hidden 에 번호를 숨기면
     * 표시값과 어긋나도 알 수 없다 (CLAUDE.md '검색 인풋에 숨은 상태를 두지 않는다').
     * 이름만 친 상태도 유효한 검색이라 포커스가 빠져도 번호로 바꾸지 않는다.
     */
    initMemberInput() {
        this.bindMemberAc('f-mem', () => this.reload());
    }

    /**
     * 회원 자동완성 인풋 하나를 붙인다.
     *
     * 위젯은 `$j.F.Admin.Util.autocomplete` — 악덕 테스트통화 모달과 같은 것이다
     * (인풋 바로 아래 드롭다운, ↓/↑/Enter/Esc, 디바운스). 예전에 쓰던
     * `$j.F.Admin.AutoComplete` 는 페이지 전역에 상태를 두고 `render_template` 로 문자열을
     * 조립하는 구형이라, 같은 화면 안에 인풋이 셋(목록·쌍 등록 2칸)이면 서로 엉킨다.
     *
     * 고르면 `이름#번호` 가 들어가고 **그 텍스트가 곧 검색기준/등록대상**이다. 테스트통화 쪽은
     * `data('pick')` 에 번호를 숨겨두는데 여기서는 따라하지 않는다 — 표시값과 어긋나도
     * 사용자가 알 수 없다 (CLAUDE.md '검색 인풋에 숨은 상태를 두지 않는다').
     */
    bindMemberAc(id, onPick) {
        const $in = $(`#${id}`);
        if (!$in.length) return;

        $j.F.Admin.Util.autocomplete($in, {
            url: '/adm/ajax/member/find/autoComplete',
            render: (r) => {
                const card = $j.F.Admin.Util.penaltyCard(r.penalty, ' ');
                const tel  = r.mem_phone ? ` <span style="color:var(--adm-c-888888, #888)">${this._esc(r.mem_phone)}</span>` : '';

                return `${card}<b>#${Util.int(r.num)}</b> ${this._esc(r.mem_name || '')}${tel}`;
            },
            pick: (r) => {
                $in.val(`${r.mem_name || ''}#${Util.int(r.num)}`);
                if (onPick) onPick();
            },
        });
    }

    /**
     * 사유 — 근거가 오더면 **본문의 오더번호에 핸들 링크**를 건다.
     *
     * 환원 사유는 `장비대금 미결제 (환원) — 오더 471364` 처럼 번호가 문장 안에 있다.
     * 본문을 정규식으로 훑지 않고 `relId`(사건의 근거 오더번호)와 일치하는 숫자만 건다 —
     * 사유는 CS 가 자유서술하는 칸이라 다른 숫자(금액·전화)가 섞일 수 있다.
     *
     * openOrderHandle 은 $j.F.Admin.Util 의 공용 메서드(기사관리 -6차 표와 같은 방식).
     */
    reasonCell(v, r) {
        const text = this._esc(v);
        if (!text) return '<span style="color:#adb5bd">사유 미기재</span>';

        const num = (r.relTable === 'order') ? Util.int(r.relId) : 0;
        if (!num || !($j.F && $j.F.Admin && $j.F.Admin.Util)) return text;

        return text.replace(new RegExp(`\\b${num}\\b`),
            `<a href="javascript:;" onclick="$j.F.Admin.Util.openOrderHandle(${num}, {name:'order_handle_${num}'})"
                title="오더 핸들 열기">${num}</a>`);
    }

    reload() {
        this.syncWho();
        if (this.$table) this.$table.ajax.reload();
    }

    /**
     * 회원번호(#1234) 클릭 → 그 회원만 모아보기.
     *
     * 목록·랭킹·수발주제한 어디서 눌러도 **제재 목록 탭**으로 데려온다. 이름을 다시 쳐서
     * 검색하는 과정을 건너뛰는 것이 목적이라, 좁힌 뒤에는 띠(#pen-who)가 지금 보고 있는
     * 회원과 [제재 관리] 버튼을 함께 보여준다.
     *
     * 번호·이름은 `data-*` 로 싣는다 — 수발주제한 행은 한 줄에 참여자가 여럿이라
     * 행 데이터에서 '그 회원' 을 집을 수 없다.
     */
    bindMemberFocus() {
        const self = this;

        $('#penalty-list, #penalty-rank, #penalty-pair').on('click', '.js-pen-only', function () {
            self.focusMember(Util.int($(this).data('no')), String($(this).data('name') || ''));
        });

        // 띠는 블레이드에 붙박이라 위임으로 잡는다 (내용은 syncWho 가 매번 새로 그린다)
        $('#pen-who').on('click', '#pen-who-open',  () => this.openWho());
        $('#pen-who').on('click', '#pen-who-clear', () => {
            $('#f-mem').val('');
            this.reload();
        });
    }

    /**
     * 목록을 한 회원으로 좁힌다.
     *
     * **검색칸에 `이름#번호` 를 넣는 것이 곧 필터다** — 숨은 상태를 따로 두지 않는다
     * (CLAUDE.md '검색 인풋에 숨은 상태를 두지 않는다'). 그래서 좁힌 뒤에도 인풋만 고치면 된다.
     *
     * 상태는 **전체**로 연다. 한 회원을 볼 때 궁금한 것은 '지금 걸린 것' 이 아니라 이력이고,
     * 집행중만 남기면 랭킹의 전과 건수와 행 수가 달라 보인다. 사유 조건도 비운다 —
     * 남아 있으면 "왜 몇 건뿐이지" 가 된다.
     */
    focusMember(mbNo, name) {
        if (!mbNo) return;

        $('#f-mem').val(`${name || ''}#${mbNo}`);
        $('#f-reason').val('');
        $('#f-state').val('');

        // 랭킹·수발주제한에서 눌렀을 수 있다. 탭을 여는 김에 표가 없으면 거기서 만들어진다
        const had = !!this.$table;
        this.openTab('list');

        if (had) this.$table.ajax.reload();   // 방금 만들어진 표는 이미 이 조건으로 받아온다
        this.syncWho();
    }

    /**
     * 회원 필터 띠 — 목록이 한 회원으로 좁혀졌을 때만 뜬다.
     * 기준은 검색칸의 `…#번호` 하나다 (인풋을 지우면 띠도 사라진다).
     */
    syncWho() {
        const $who = $('#pen-who');
        if (!$who.length) return;

        const m = String($('#f-mem').val() || '').trim().match(/^(.*?)#(\d+)$/);
        if (!m) { $who.prop('hidden', true).empty(); return; }

        const no   = Util.int(m[2]);
        const name = this._esc($.trim(m[1])) || `#${no}`;

        // 문구는 한 덩어리로 묶는다 — 낱개로 두면 flex 가 낱말마다 간격을 벌린다
        $who.html(`<span><b>${name}</b> <span class="text-muted">#${no}</span> 의 제재만 보고 있습니다.</span>
            <button type="button" class="btn btn-sm btn-danger" id="pen-who-open">제재 관리</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" id="pen-who-clear">전체 보기</button>`).prop('hidden', false);
    }

    /** 띠의 [제재 관리] — 지금 좁혀 놓은 회원의 모달을 연다 */
    openWho() {
        const m = String($('#f-mem').val() || '').trim().match(/^(.*?)#(\d+)$/);
        if (!m) return;

        $j.F.Admin.Member.Penalty.openFor(Util.int(m[2]), $.trim(m[1]), () => this.afterChange());
    }

    /**
     * 원장이 바뀌면 열려 있는 표를 다시 받는다.
     *
     * 부르는 곳이 둘 — 기사관리 모달의 부과·해제·사유수정, 그리고 **감시 탭의 판정**이다.
     * 판정은 곧 제재 등록이라 같은 화면의 다른 탭들이 바로 달라진다.
     */
    afterChange() {
        if (this.$table) this.$table.ajax.reload(null, false);
        if (this.$rank)  this.$rank.ajax.reload(null, false);
        if (this.$ban)   this.$ban.ajax.reload(null, false);
    }

    load() {
        const self = this;

        this.$table = new DataTable('#penalty-list', {
            autoWidth: false,
            serverSide: true,        // 처벌이 1,300행이라 전량을 내려받지 않는다
            processing: true,
            searching: false,        // 검색은 위 툴바가 서버로 보낸다
            ordering: false,         // 정렬은 서버가 정한다 (집행중 → 최근순)
            pageLength: 50,
            lengthChange: false,
            ajax: {
                url: '/adm/ajax/penalty/list',
                type: 'post',
                data: (d) => {
                    d.state  = $('#f-state').val();
                    d.charge = $('#f-charge').val();
                    d.punish = $('#f-punish').val();
                    d.mem    = $.trim($('#f-mem').val());
                    d.reason = $.trim($('#f-reason').val());
                },
            },
            columns: [
                { data: 'openedAt', title: '일자', width: '92px',
                  render: (v) => self._nowrap(v) || '<span style="color:#ced4da">날짜없음</span>' },
                { data: null, title: '회원', width: '190px',   // 이름 + 번호 + [모아보기]
                  render: (d, t, r) => self.memberCell(r) },
                { data: 'chargeTitle', title: '죄목', width: '150px',   // 죄목 + [수정]
                  render: (v, t, r) => self.chargeCell(v, r) },
                { data: null, title: '처벌', width: '150px',
                  render: (d, t, r) => self.punishCell(r) },
                { data: 'reason', title: '사유',
                  render: (v, t, r) => self.reasonCell(v, r) },
                { data: 'manageName', title: '담당', width: '80px',
                  render: (v) => self._nowrap(v) || '<span style="color:#adb5bd">시스템</span>' },
                { data: null, title: '상태', width: '96px',
                  render: (d, t, r) => r.active
                      ? '<span class="badge badge-danger">집행중</span>'
                      : `<span class="text-muted" style="white-space:nowrap">해제 ${self._esc(r.releasedAt)}</span>` },
            ],
            language: {
                info: '_START_–_END_ / 전체 _TOTAL_건',
                infoEmpty: '내역 없음',
                emptyTable: '해당 조건의 제재가 없습니다.',
                processing: '불러오는 중…',
                paginate: { first: '«', previous: '‹', next: '›', last: '»' },
            },
            rowCallback: (row, data) => {
                if (!data.active) $(row).css({ opacity: .6 });
            },
        });
    }

    /**
     * 죄목 셀 — 누르면 그 회원의 **제재 관리 모달**이 열린다 (기사편집의 그 모달).
     *
     * 목록·랭킹은 "지금 누가 막혀 있나" 만 답하고, 왜·무엇을 풀지는 회원 단위라야 정한다.
     * 회원명은 이미 기사편집을 여는 자리라(memberCell) 두 동선이 겹치지 않게 죄목에 붙였다.
     * 회원번호·이름은 `data-*` 가 아니라 DataTables 행 데이터에서 읽는다 (위임 핸들러 참고).
     */
    chargeCell(text, r) {
        const label = this._esc(text);
        if (!label) return '<span style="color:#adb5bd">—</span>';

        return `<a href="javascript:;" class="js-pen-open pen-open" title="제재 관리 열기">${label}</a>`
             + this.caseEdit(r);
    }

    /**
     * 행에서 곧바로 여는 [수정] — 죄목·처벌·사유를 한 창에서 고친다.
     *
     * 예전에는 죄목을 눌러 그 회원의 제재 관리 모달을 열고, 거기 표에서 방금 보던 줄을
     * 다시 찾아 연필을 눌러야 했다. 목록의 한 줄이 곧 사건이라 한 단계가 통째로 군더더기였다.
     * 회원 모달로 가는 길(죄목 클릭)은 그대로 둔다 — "이 회원 전체를 본다"는 다른 용무다.
     *
     * 여는 창·저장 경로는 모달의 그것과 **같은 것**이다($j.F.Admin.Member.Penalty.openCase).
     * 화면마다 편집창을 새로 만들지 않는다.
     *
     * 노출 조건은 서버가 준 플래그 그대로다 — 모달의 caseEdit() 과 같은 식으로 계산해서
     * 내려온다(PenaltyService::adminList). 화면이 자체 판단을 하면 목록에선 보이는데
     * 눌러보면 막히는 버튼이 생긴다.
     *   canEditReason  환원 같은 자동 죄목은 배치가 소유한다
     *   active && !canRelease  쌍 제한이 집행중인 사건 (혼자 풀 수 없다)
     */
    caseEdit(r) {
        if (!r || !r.canEditReason) return '';
        if (r.active && !r.canRelease) return '';

        return ` <button type="button" class="me-pen__edit js-pen-edit" title="제재 수정">수정</button>`;
    }

    /** 랭킹의 죄목 — 배지 묶음 전체가 하나의 열기 버튼이다 */
    chargeBadges(r) {
        const list = r.charges || [];
        if (!list.length) return '<span style="color:#adb5bd">—</span>';

        const badges = list.map((c) => `<span class="badge badge-secondary mr-1">${this._esc(c)}</span>`).join('');

        return `<a href="javascript:;" class="js-pen-open pen-open pen-open--badges" title="제재 관리 열기">${badges}</a>`;
    }

    /** 죄목 클릭 → 제재 관리 모달. 표가 다시 그려져도 살아 있게 위임으로 잡는다 */
    bindChargeOpen() {
        const self = this;

        $('#penalty-list, #penalty-rank').on('click', '.js-pen-open', function () {
            const api = $(this).closest('table').DataTable();
            const row = api.row($(this).closest('tr')).data();
            if (!row) return;

            $j.F.Admin.Member.Penalty.openFor(row.mbNo, row.memName, () => self.afterChange());
        });

        // 행의 [수정] — 회원 모달을 거치지 않고 그 사건의 수정창을 바로 띄운다
        $('#penalty-list').on('click', '.js-pen-edit', function () {
            const api = $(this).closest('table').DataTable();
            const row = api.row($(this).closest('tr')).data();
            if (!row) return;

            $j.F.Admin.Member.Penalty.openCase(row.mbNo, row.memName, row.caseNum, () => self.afterChange());
        });
    }

    /**
     * 회원칸 — 두 동선이 붙어 있다.
     *   이름       기사편집 (부과·해제는 거기서)
     *   [모아보기] 이 회원의 제재만 → focusMember
     *
     * 모아보기는 **버튼으로 세운다.** 처음엔 회원번호(#4904)에 링크를 걸었는데, 번호는
     * 원래 읽는 값이라 누를 수 있다는 게 드러나지 않았다.
     *
     * @param {boolean} focus 모아보기 버튼을 달지 — 수발주제한 목록은 한 줄에 참여자가
     *                        여럿이라 버튼이 그만큼 늘어서 끈다
     */
    memberCell(r, focus = true) {
        const no   = Util.int(r.mbNo);
        const name = this._esc(r.memName) || `#${no}`;

        const btn = focus
            ? ` <button type="button" class="js-pen-only pen-only" data-no="${no}"
                        data-name="${this._esc(r.memName || '')}"
                        title="이 회원의 제재만 모아보기">모아보기</button>`
            : '';

        // 이름 = 기사편집 바로(일부러 둔 동선) · #번호 = 회원 팝오버
        return `<a href="javascript:$j.F.Admin.Util.memberEdit(${no})"><b>${name}</b></a>`
             + ` <span class="text-muted" style="font-size:11px">#${$j.F.Admin.Member.Pop.cellNo(no)}</span>${btn}`;
    }

    /** 처벌칸 — 경고는 막는 게 없으므로 그 사실을 적는다 */
    punishCell(r) {
        const badge = `<span class="badge badge-${r.level >= 2 ? 'danger' : 'warning'}" style="white-space:nowrap">${this._esc(r.punishTitle)}</span>`;
        // 짜고치기는 같은 패의 다른 참여자를 붙인다 (2명이면 예전 '↔ 상대' 와 같은 모양)
        const mates = (r.mates || []).map((m) => $j.F.Admin.Member.Pop.cell(Util.int(m.mbNo), m.memName));
        const tail  = mates.length
            ? `<span class="text-muted" style="font-size:11px"> ↔ ${mates.join(' · ')}</span>`
            : (r.blocks ? '' : '<span class="text-muted" style="font-size:11px"> 차단없음</span>');

        return badge + tail;
    }

    /**
     * 수발주제한 등록 폼 — 참여자 **N명**을 대등하게 고른다.
     *
     * 기본 2칸으로 시작하고 [+ 회원] 으로 늘린다. 짜고치기가 셋 이상인 경우를 쌍으로 쪼개
     * 등록하면 "이 셋이 한 패" 라는 사실이 어디에도 안 남는다 (원장도 패 단위다).
     *
     * 감시의 짜고치기 판정도 같은 원장에 쓴다 — 이 폼은 감시가 못 잡은 건을 손으로 넣는 자리다.
     */
    initBanForm() {
        this.memSeq = 0;
        this.addMemberInput();
        this.addMemberInput();

        this.bindMemberAc('p-find', () => this.$ban && this.$ban.ajax.reload());

        $('#p-more').on('click', () => this.addMemberInput().trigger('focus'));
        $('#p-add').on('click', () => this.addBan());
        $('#p-reason').on('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.addBan(); } });
    }

    /** 참여자 인풋 한 칸 추가 — 3칸째부터는 지울 수 있다(2명이 최소라서) */
    addMemberInput() {
        const id  = `p-mem${++this.memSeq}`;
        const del = this.memSeq > 2
            ? `<button type="button" class="btn btn-link p-0 js-mem-del" title="이 칸 제거"
                       style="position:absolute;right:6px;top:6px;line-height:1;color:#adb5bd">✕</button>`
            : '';

        const $wrap = $(`<span class="p-mem-slot" style="position:relative;display:inline-block">
                <input type="text" class="form-control" id="${id}" autocomplete="off"
                       placeholder="회원${this.memSeq} (이름으로 검색)" style="width:210px">${del}
            </span>`).appendTo('#p-mems');

        $wrap.on('click', '.js-mem-del', () => $wrap.remove());
        this.bindMemberAc(id);

        return $wrap.find('input');
    }

    /** `이름#번호` 에서 번호만 — 인풋에 보이는 값이 곧 대상이다 */
    _pickedNo(id) {
        const m = String($(`#${id}`).val() || '').match(/#(\d+)\s*$/);
        return m ? Util.int(m[1]) : 0;
    }

    addBan() {
        // 채워진 칸만 본다 — [+ 회원] 을 눌러놓고 안 쓴 칸은 무시한다
        const picked = [];
        const labels = [];
        $('#p-mems input').each((i, el) => {
            const no = this._pickedNo(el.id);
            if (!no) return;
            if (picked.indexOf(no) === -1) { picked.push(no); labels.push($(el).val()); }
        });

        const reason = $.trim($('#p-reason').val());

        if (picked.length < 2) {
            return $j.F.Admin.Util.tsalert('error', '회원을 2명 이상 목록에서 골라주세요. (이름만 입력하면 등록되지 않습니다)');
        }
        if (!reason) { $j.F.Admin.Util.tsalert('error', '사유를 입력하세요.'); return $('#p-reason').trigger('focus'); }

        if (!confirm(`${labels.join(' ↔ ')}\n\n이 회원들끼리 서로 수발주를 막습니다.\n참여자 각자의 제재현황에 기록됩니다.`)) return;

        $.post('/adm/ajax/member/relation/add', { mb_nos: picked, memo: reason })
            .done((res) => {
                if (!res.result) return $j.F.Admin.Util.tsalert('error', '이미 걸려 있거나 등록에 실패했습니다.');
                $j.F.Admin.Util.tsalert('success', '수발주제한을 등록했습니다.');
                $('#p-mems input').val('');
                $('#p-mems .p-mem-slot').slice(2).remove();   // 늘린 칸은 원래대로
                $('#p-reason').val('');
                this.$ban.ajax.reload();
            })
            .fail(() => $j.F.Admin.Util.tsalert('error', '등록 중 오류가 발생했습니다.'));
    }

    /** 수발주제한 목록 — **한 패가 한 줄** (원장에는 참여자마다 사건+처벌이 하나씩 있다) */
    loadBan() {
        const self = this;

        this.$ban = new DataTable('#penalty-pair', {
            autoWidth: false,
            processing: true,
            searching: false,
            ordering: false,
            pageLength: 50,
            lengthChange: false,
            ajax: {
                url: '/adm/ajax/member/relation',
                type: 'post',
                data: (d) => { d.findMbNumber = self._pickedNo('p-find') || ''; },
            },
            columns: [
                { data: 'reg_date', title: '일자', width: '110px',
                  render: (v) => self._nowrap(String(v || '').substring(0, 10)) },
                { data: null, title: '참여자', width: '320px',
                  render: (d, t, r) => (r.members || [])
                      .map((m) => self.memberCell({ mbNo: m.mbNo, memName: m.memName }, false))
                      .join('<span style="color:#adb5bd"> ↔ </span>') },
                { data: 'memo', title: '사유',
                  render: (v) => self._esc(v) || '<span style="color:#adb5bd">사유 미기재</span>' },
                { data: 'manage_name', title: '등록자', width: '80px',
                  render: (v) => self._nowrap(v) || '<span style="color:#adb5bd">시스템</span>' },
                { data: null, title: '해제', width: '70px',
                  render: (d, t, r) => `<button type="button" class="btn btn-sm btn-outline-danger js-pair-del" data-num="${Util.int(r.num)}">해제</button>` },
            ],
            language: {
                info: '_START_–_END_ / 전체 _TOTAL_건',
                infoEmpty: '내역 없음',
                emptyTable: '등록된 수발주제한이 없습니다.',
                processing: '불러오는 중…',
                paginate: { first: '«', previous: '‹', next: '›', last: '»' },
            },
        });

        $('#penalty-pair').on('click', '.js-pair-del', function () {
            if (!confirm('이 수발주제한을 해제합니다. 패 전체가 함께 풀립니다.')) return;
            $.ajax({ url: '/adm/ajax/member/relation/delete', type: 'delete', data: { num: Util.int($(this).data('num')) } })
                .done((res) => {
                    if (!res.result) return $j.F.Admin.Util.tsalert('error', '해제하지 못했습니다.');
                    $j.F.Admin.Util.tsalert('success', '해제했습니다.');
                    self.$ban.ajax.reload();
                })
                .fail(() => $j.F.Admin.Util.tsalert('error', '해제 중 오류가 발생했습니다.'));
        });
    }

    /** 랭킹 — 회원별 집계. "누가 반복하나" 를 본다 */
    loadRanking() {
        const self = this;

        this.$rank = new DataTable('#penalty-rank', {
            autoWidth: false,
            serverSide: true,
            processing: true,
            searching: false,
            lengthChange: false,
            pageLength: 50,

            // 정렬은 **표 헤더**가 한다. 예전엔 위에 select 를 뒀는데 표에 이미 그 컬럼들이
            // 있어 같은 말이 두 번이었다 (서버는 order[0] 을 읽어 화이트리스트로 매핑한다).
            // 기본은 경고 횟수 — 전과로 두면 환원(미결제) 회원이 상위를 채워
            // "반복해서 문제를 일으키는 회원" 이 안 보인다.
            ordering: true,
            order: [[3, 'desc']],

            // 첫 클릭은 **내림차순**. DataTables 기본은 오름차순이라 그대로 두면 '전과 1건'
            // 회원이 맨 위로 올라온다 — 이 표에서 궁금한 건 늘 '가장 많은 쪽' 이다.
            columnDefs: [{ targets: '_all', orderSequence: ['desc', 'asc'] }],

            ajax: {
                url: '/adm/ajax/penalty/ranking',
                type: 'post',
            },
            columns: [
                // 순위는 서버가 준 표시순서 그대로다 — 이 컬럼으로 정렬하면 뜻이 순환한다
                { data: 'rank', title: '#', width: '50px', orderable: false },
                { data: null, title: '회원', width: '200px', orderable: false,
                  render: (d, t, r) => self.memberCell(r) },
                { data: 'priorCnt',  title: '전과',   width: '70px', className: 'text-right',
                  render: (v) => `<b>${Util.int(v)}</b>` },
                { data: 'warnCnt', title: '경고', width: '90px', className: 'text-right',
                  render: (v, t, r) => Util.int(v) === 0 ? '<span class="text-muted">0</span>'
                      : `${Util.int(v)}${r.isWatch ? ' <span class="badge badge-danger">요주의</span>' : ''}` },
                { data: 'activeCnt', title: '집행중', width: '80px', className: 'text-right',
                  render: (v) => Util.int(v) === 0 ? '<span class="text-muted">0</span>'
                      : `<span class="badge badge-danger">${Util.int(v)}</span>` },
                { data: null, title: '죄목', orderable: false,
                  render: (d, t, r) => self.chargeBadges(r) },
                { data: 'lastAt', title: '최근', width: '96px',
                  render: (v) => self._nowrap(v) || '<span style="color:#ced4da">날짜없음</span>' },
            ],
            language: {
                info: '_START_–_END_ / 전체 _TOTAL_명',
                infoEmpty: '내역 없음',
                emptyTable: '제재를 받은 회원이 없습니다.',
                processing: '불러오는 중…',
                paginate: { first: '«', previous: '‹', next: '›', last: '»' },
            },
        });
    }

    _nowrap(s) {
        const v = this._esc(s);
        return v ? `<span style="white-space:nowrap">${v}</span>` : '';
    }

    _esc(s) {
        return String(s ?? '').replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    }
}
